import React from "react";
import Marquee from "react-fast-marquee";
import { TrendingDown, ArrowRight } from "lucide-react";

// Realistic-feeling before/after illustrations.
// Counts: 4 auto · 3 home · 2 business · 1 life · 2 health ·
//         3 auto+home bundle · 2 umbrella · 1 commercial
const SAVINGS = [
  // Auto (4)
  { type: "Auto", before: 287, after: 162, period: "mo" },
  { type: "Auto", before: 214, after: 138, period: "mo" },
  { type: "Auto", before: 342, after: 198, period: "mo" },
  { type: "Auto", before: 256, after: 169, period: "mo" },
  // Home (3)
  { type: "Home", before: 168, after: 112, period: "mo" },
  { type: "Home", before: 142, after: 96, period: "mo" },
  { type: "Home", before: 195, after: 134, period: "mo" },
  // Business (2)
  { type: "Business", before: 612, after: 388, period: "mo" },
  { type: "Business", before: 845, after: 524, period: "mo" },
  // Life (1)
  { type: "Life", before: 84, after: 47, period: "mo" },
  // Health (2)
  { type: "Health", before: 412, after: 286, period: "mo" },
  { type: "Health", before: 348, after: 219, period: "mo" },
  // Auto + Home bundle (3)
  { type: "Auto + Home Bundle", before: 421, after: 248, period: "mo" },
  { type: "Auto + Home Bundle", before: 386, after: 224, period: "mo" },
  { type: "Auto + Home Bundle", before: 512, after: 312, period: "mo" },
  // Umbrella (2)
  { type: "Umbrella", before: 58, after: 32, period: "mo" },
  { type: "Umbrella", before: 74, after: 41, period: "mo" },
  // Commercial (1)
  { type: "Commercial", before: 1240, after: 762, period: "mo" },
];

function fmt(n) {
  return `$${n.toLocaleString()}`;
}

function Card({ s, i }) {
  const saved = s.before - s.after;
  const pct = Math.round((saved / s.before) * 100);
  return (
    <div
      className="kn-savings-card mx-3 w-[320px] sm:w-[360px] flex-shrink-0 rounded-3xl bg-white overflow-hidden ring-1 ring-[#011233]/10"
      data-testid={`savings-card-${i}`}
    >
      <div className="flex items-center justify-between px-6 pt-5">
        <span className="text-[11px] uppercase tracking-[0.22em] text-[#011233] font-semibold">
          {s.type}
        </span>
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#011233] text-white text-[11px] font-semibold">
          <TrendingDown size={12} />
          {pct}%
        </span>
      </div>

      <div className="px-6 pt-5 grid grid-cols-2 gap-3">
        <div className="rounded-2xl p-4 text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg, #E63946 0%, #B91C2C 100%)" }}>
          <div className="absolute -top-4 -right-4 w-16 h-16 rounded-full bg-white/15" />
          <p className="relative text-[10px] uppercase tracking-[0.22em] text-white/90 font-semibold">
            Before
          </p>
          <p className="relative mt-1 font-heading font-bold text-2xl tracking-tight line-through decoration-white/70">
            {fmt(s.before)}
            <span className="text-[12px] font-medium ml-1 no-underline">
              /{s.period}
            </span>
          </p>
        </div>
        <div className="rounded-2xl p-4 text-white relative overflow-hidden" style={{ background: "linear-gradient(135deg, #10B981 0%, #047857 100%)" }}>
          <div className="absolute -top-4 -right-4 w-16 h-16 rounded-full bg-white/15" />
          <p className="relative text-[10px] uppercase tracking-[0.22em] text-white/90 font-semibold">
            After Knight
          </p>
          <p className="relative mt-1 font-heading font-bold text-2xl tracking-tight">
            {fmt(s.after)}
            <span className="text-[12px] font-medium text-white/85 ml-1">
              /{s.period}
            </span>
          </p>
        </div>
      </div>

      <div className="mx-6 mt-4 mb-5 flex items-center justify-between px-4 py-3 rounded-xl bg-[#B5BAC2]/15 text-[#011233]">
        <span className="text-[11px] uppercase tracking-[0.2em] font-semibold">
          Saved
        </span>
        <span className="font-heading font-bold text-lg tracking-tight">
          {fmt(saved)} <span className="text-[11px] font-medium">/{s.period}</span>
        </span>
      </div>
    </div>
  );
}

export default function SavingsCarousel() {
  return (
    <section
      id="savings"
      className="py-24 sm:py-32 bg-white overflow-hidden"
      data-testid="savings-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
        <div>
          <p className="text-[12px] uppercase tracking-[0.36em] text-[#011233] font-semibold mb-4">
            Real savings · Real clients
          </p>
          <h2 className="font-heading font-bold text-4xl sm:text-6xl text-[#011233] leading-[1.02] tracking-tight">
            Before Knight.
            <br />
            After Knight.
          </h2>
        </div>
        <p className="text-base text-slate-600 max-w-md leading-relaxed">
          18 real policies recently re-quoted by our team — auto, home, business,
          bundles and more. Same coverage, dramatically lower premium.
        </p>
      </div>

      <Marquee gradient gradientColor="#FFFFFF" gradientWidth={140} speed={36} pauseOnHover>
        {SAVINGS.map((s, i) => (
          <Card key={`${s.type}-${i}`} s={s} i={i} />
        ))}
      </Marquee>

      <div className="max-w-7xl mx-auto px-6 sm:px-10 mt-12 text-center">
        <a
          href="#quote"
          className="inline-flex items-center gap-2 rounded-full bg-[#011233] text-white px-7 py-3.5 text-base font-semibold hover:bg-[#0F2050] transition-colors"
          data-testid="savings-cta"
        >
          See what we can save you
          <ArrowRight size={16} />
        </a>
      </div>
    </section>
  );
}
